import { Downloader } from './internal/Downloader';
import { SpeedTestProgressListener, SpeedTestResult, QualityURLSet } from './SpeedTestProgressListener';
import { createError } from '../error/Error';
import { ErrorCodes } from '../error/ErrorCodes';
import { SpeedAnalyserErrorCodes } from './internal/SpeedAnalyserErrorCodes';

/**
 * Speed Analyser module for measuring network download speed using MP4 files.
 *
 * This module provides network speed measurement capabilities specifically designed
 * for OTT (Over-The-Top) streaming applications. It downloads test files sequentially
 * and provides real-time progress updates and final speed measurements.
 *
 * Features:
 * - Sequential download of multiple test URLs
 * - Real-time progress reporting (every 1 second)
 * - Industry-standard speed calculation (transfer time only)
 * - Fixed-duration window (15 seconds per download)
 * - DNS resolution time measurement
 * - Comprehensive error handling
 */
export class SpeedAnalyser {
  private downloader?: Downloader;
  private isRunning = false;

  /**
   * Measures download speed using the provided test URLs.
   *
   * Downloads are performed sequentially in the order: low, medium, high.
   * Each download has a maximum duration of 15 seconds to prevent excessive
   * data usage and ensure consistent results.
   *
   * @param qualityUrlSet Set of URLs categorized by quality/bandwidth tiers (low, medium, high)
   * @param listener Progress listener to receive updates and results
   * @param timeoutPerFile Optional timeout per file in milliseconds (default: 15000ms)
   */
  public async measureDownloadSpeed(
    qualityUrlSet: QualityURLSet,
    listener: SpeedTestProgressListener,
    timeoutPerFile = 15000,
  ): Promise<void> {
    if (this.isRunning) {
      // Already running is a user error, use fl-foundation error pattern
      const error = createError(
        SpeedAnalyserErrorCodes.SPEED_TEST_DETECTION_FAILURE | ErrorCodes.ERROR_CATEGORY_MASK_THIRDPARTY,
        'Speed test is already running. Please wait for completion or call stopTest().',
      );
      listener.onError(error);
      return;
    }

    if (!qualityUrlSet || (!qualityUrlSet.low && !qualityUrlSet.medium && !qualityUrlSet.high)) {
      // No URLs is a configuration error, use fl-foundation error pattern
      const error = createError(
        SpeedAnalyserErrorCodes.SPEED_TEST_DETECTION_FAILURE | ErrorCodes.ERROR_CATEGORY_MASK_THIRDPARTY,
        'Quality URL set must contain at least one URL (low, medium, or high).',
      );
      listener.onError(error);
      return;
    }

    // Convert QualityURLSet to array for internal processing, filtering out empty URLs
    const urls = [qualityUrlSet.low, qualityUrlSet.medium, qualityUrlSet.high].filter(url => url && url.trim() !== '');

    this.isRunning = true;

    try {
      // Create downloader with progress listener
      this.downloader = new Downloader(listener);

      // Start the download test
      const result = await this.downloader.downloadSequentially(urls, timeoutPerFile);

      // Convert internal result to public interface
      const speedTestResult: SpeedTestResult = {
        averageSpeedMbps: result.averageSpeedMbps,
        avgDNSResolutionTimeMs: result.avgDnsResolutionTimeMs,
        timeTakenForTestMs: result.totalTestDurationMs,
      };

      // Report completion - even if speed is 0, it's still a valid result
      listener.onComplete(speedTestResult);
    } catch (error) {
      // Check if this is already a properly masked fl-foundation error
      if (error && typeof error === 'object' && 'errorMessage' in error && 'errorCode' in error) {
        // This is already a masked error from the Downloader, pass it through directly
        listener.onError(error as any);
      } else {
        // This is an unexpected error, create a general error using fl-foundation pattern
        const generalError = createError(
          SpeedAnalyserErrorCodes.SPEED_TEST_DETECTION_FAILURE | ErrorCodes.ERROR_CATEGORY_MASK_THIRDPARTY,
          'All speed test URLs failed. Unable to measure network speed.',
        );
        listener.onError(generalError);
      }
    } finally {
      this.isRunning = false;
      this.downloader = undefined;
    }
  }

  /**
   * Stops the currently running speed test.
   *
   * This will abort all ongoing downloads and complete the test with
   * results from any completed downloads.
   */
  stopTest(): void {
    if (this.downloader) {
      this.downloader.abort();
    }
    this.isRunning = false;
  }

  /**
   * Returns true if a speed test is currently running.
   */
  isTestRunning(): boolean {
    return this.isRunning;
  }

  /**
   * Factory method to create a new SpeedAnalyser instance.
   *
   * @returns New SpeedAnalyser instance
   */
  static create(): SpeedAnalyser {
    return new SpeedAnalyser();
  }
}
